import React, { useMemo, useState } from 'react';
import { Modal, TextInput, Group, Button, Select, SimpleGrid, Input, FileInput, Table, Text, ActionIcon } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import { useSelector } from 'react-redux';
import { URL_PREFIX } from '../constants/stores';

// Define prop types for the ModalForm component
interface ModalFormProps {
    opened: boolean;
    onClose: () => void;
    setTrailers: (value: any) => void;
}

const ModalForm: React.FC<ModalFormProps> = ({ opened, onClose, setTrailers }) => {
    const companies = useMemo(() => ['SMARTWAY', 'G2000', 'SVP SPORTS', 'OTHERS'], [])
    const newProduct = { label: '', description: '', skid: 0 };
    const [products, setProducts] = useState<{ label: string, description: string, skid: number }[]>([newProduct])
    const token = useSelector((state: any) => state.user.sessionId);
    const [mapInfo, setMapInfo] = useState({ lat: '', lon: '', x: 0, y: 0, width: 50, height: 100, orientation: 'horizontal', status: '', trailer_no: '' })
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            trailer_no: '',
            rental_company: 'SMARTWAY',
            lease_agreement_no: '',
            received_date: new Date().toISOString().slice(0, 10),
            note: '',
            images: [],

        },
        validate: {
            // trailer_no should be neither empty nor null 
            trailer_no: (value: string) => value ? null : 'Trailer number is required',
            rental_company: (value: string) => value ? null : 'Rental company is required',
            received_date: (value: string) => value ? null : 'Date is required',
        },
    });
    
    // Add new product item to the products array
    const addProduct = () => {
        setProducts((prevProducts) => [...prevProducts, newProduct]);
    };

    // Remove product item from the products array
    const removeProduct = (index: number) => {
        setProducts((prevProducts) => prevProducts.filter((_, i) => i !== index));
    };

    const saveData = async (trailerData: any) => {
        try {
            const response = await fetch(`${URL_PREFIX}/trailer/create`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...trailerData, info: {
                        ...mapInfo,
                        trailer_no: trailerData.trailer_no
                    }
                }),
            });
            const { data, message } = await response.json();
            if (response.status === 200) {
                setTrailers((prev: any) => [...prev, data]);
                onClose();
                // reset the values
                form.reset();
                setProducts([newProduct]);
            }
            alert(message);
        } catch (error) {
            console.log('Error creating entry:', error);
            alert('Errr creating entry');
        }
    }

    const onSubmit = (values: any) => {
        // Validate products
        for (const [index, product] of products.entries()) {
            if (!product.label) {
                alert(`Please fill in all fields for product ${index + 1}`);
                return; // Stop form submission
            }
        }
        saveData({ ...values, products });
    }

    return (
        <Modal opened={opened} onClose={onClose} title="My Form" size={'xl'} centered>
            <form onSubmit={form.onSubmit(onSubmit)}>
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing={'md'}>
                    <TextInput
                        withAsterisk
                        label="Trailer No"
                        placeholder="Trailer no"
                        key={form.key('trailer_no')}
                        {...form.getInputProps('trailer_no')}
                    />
                    <TextInput
                        label="Lease Agreement No"
                        placeholder="Lease Agreement No"
                        key={form.key('lease_agreement_no')}
                        {...form.getInputProps('lease_agreement_no')}
                    />
                    <TextInput
                        withAsterisk
                        type='date'
                        label="Received Date"
                        placeholder="Received Date"
                        key={form.key('received_date')}
                        {...form.getInputProps('received_date')}
                    />
                    <Select
                        withAsterisk
                        data={companies}
                        label="Rental Company"
                        placeholder="Rental Company"
                        key={form.key('rental_company')}
                        {...form.getInputProps('rental_company')}
                    />
                    <TextInput
                        label="Invoice To"
                        placeholder="Invoice To"
                        key={form.key('payer')}
                        {...form.getInputProps('payer')}
                    />
                    {/* <FileInput
                        label={'Image'}
                        key={form.key('images')}
                        multiple
                    /> */}
                    <Select
                        withAsterisk
                        data={['horizontal', 'vertical']}
                        label="Orientation"
                        placeholder="tation"
                        value={mapInfo.orientation}
                        onChange={(e) =>
                            setMapInfo({ ...mapInfo, orientation: e as 'horizontal' | 'vertical', height: e === 'vertical' ? 50 : 100, width: e === 'vertical' ? 100 : 50 })}
                    />
                    <TextInput
                        label="Trailer status"
                        placeholder='Status related to trailer'
                        value={mapInfo.status}
                        onChange={(e) => setMapInfo({ ...mapInfo, status: e.target.value })}
                    />
                </SimpleGrid>
                <Table style={{ marginTop: '1rem' }} withRowBorders withColumnBorders withTableBorder>
                    <Table.Thead>
                        <Table.Th className='icon' style={{ width: '20px' }} onClick={addProduct}><IconPlus /></Table.Th>
                        <Table.Th className='expandedTh'>Product</Table.Th>
                        <Table.Th className='expandedTh'>Description</Table.Th>
                        <Table.Th className='expandedTh'>Skids</Table.Th>
                    </Table.Thead>
                    <Table.Tbody>
                        {products.map((product, index) => (
                            <Table.Tr key={index}>
                                <Table.Td onClick={() => removeProduct(index)}>
                                    <IconTrash className='icon' color='red' />
                                </Table.Td>
                                <Table.Td>
                                    <TextInput
                                        variant="unstyled"
                                        value={product.label}
                                        onChange={(e) =>
                                            setProducts((prevProducts) =>
                                                prevProducts.map((p, i) =>
                                                    i === index ? { ...p, label: e.target.value } : p
                                                )
                                            )
                                        }
                                    />
                                </Table.Td>
                                <Table.Td>
                                    <TextInput
                                        variant="unstyled"
                                        value={product.description}
                                        onChange={(e) =>
                                            setProducts((prevProducts) =>
                                                prevProducts.map((p, i) =>
                                                    i === index ? { ...p, description: e.target.value } : p
                                                )
                                            )
                                        }
                                    />
                                </Table.Td>
                                <Table.Td>
                                    <TextInput
                                        variant="unstyled"
                                        type="number"
                                        value={product.skid}
                                        onChange={(e) =>
                                            setProducts((prevProducts) =>
                                                prevProducts.map((p, i) =>
                                                    i === index ? { ...p, skid: Number(e.target.value) } : p
                                                )
                                            )
                                        }
                                    />
                                </Table.Td>
                            </Table.Tr>
                        ))}
                        <Table.Tr></Table.Tr>
                    </Table.Tbody>
                </Table>
                <TextInput
                    label="Note"
                    placeholder="Note"
                    key={form.key('note')}
                    {...form.getInputProps('note')}
                />
                <Group justify="flex-end" mt="md">
                    <Button type="submit">Submit</Button>
                </Group>
            </form>
        </Modal>
    );
};


export default ModalForm;